import {
    AfterViewInit,
    ChangeDetectionStrategy,
    ChangeDetectorRef,
    Component,
    ElementRef,
    EventEmitter,
    Input,
    OnInit,
    Output,
    ViewChild,
    ViewEncapsulation
} from '@angular/core';
import {FormControl, Validators} from '@angular/forms';
import {TooltipService} from '@irb-common/components/tooltip/tooltip.service';
import {debounceTime, distinctUntilChanged} from "rxjs/operators";
import {conditionalValidator} from '@irb-common/validators/conditionalValidator';
import {DecimalPipe} from '@angular/common';
import {NegativeNumberPipe} from '@irb-common/pipes/NegativeNumberPipe';

@Component({ standalone: false,
  selector: 'irb-number-text-box',
  templateUrl: './number-text-box.component.html',
  styleUrls: ['./number-text-box.component.scss'],
  encapsulation: ViewEncapsulation.None,
  providers: [NegativeNumberPipe],
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class NumberTextBoxComponent implements OnInit, AfterViewInit {
  @ViewChild("inputField") inputField: ElementRef;
  private _value: number;
  private _isCurrency: boolean = false;
  private _isPercentage: boolean = false;
  private _isRequired: boolean = false;
  private _isNegative: boolean = false;
  private _isNotApplicable: boolean = false;
  private _places: number = 0;
  private _displayPlaces: number = 0;
  private _min: number;
  private _max: number;
  private _minConstraint: string;
  private _maxConstraint: string;
  private _disabled: boolean = false;
  displayValue: string = '';
  isEntryMode: boolean = false;
  tooltipMessage: string;
  inputControl: FormControl;

  @Output() change: EventEmitter<number> = new EventEmitter<number>();
  @Output() valueChange: EventEmitter<number> = new EventEmitter<number>();
  @Output() applyChange: EventEmitter<any> = new EventEmitter<any>();
  @Input() invalidMessage: string = "Invalid amount";
  @Input() invalidRangeMessage: string;
  @Input() invalidRequiredMessage: string = "This field is required";
  @Input() placeholder: string = "";
  @Input() invalidMinConstraintMessage: string = "";
  @Input() invalidMaxConstraintMessage: string = "";
  @Input() width: string = "80px";
  @Input() rounding: boolean = false;
  @Input() autofocus: boolean = false;
  @Input() isBold: boolean = false;
  @Input() addZeroIfDotFirst: boolean = false;
  @Input() editIconOnHover: boolean = false;

  @Input()
  set value(n: any) {
    // sa 2021-08-17 this complex conditional structure ensures that tests don't fail
    if (typeof n === 'undefined') {
      this._value = undefined;
    } else if (n === null || isNaN(n) || n === '') {
      this._value = null;
    } else {
      this._value = Number(n);
    }
    this.onValueChange();
  }

  get value(): number {
    return this._value;
  }

  @Input()
  set isRequired(value: boolean) {
    this._isRequired = value || false;
    this.applyValidators();
    this.updateDisplayValue();
  }

  get isRequired(): boolean {
    return this._isRequired;
  }

  @Input()
  set isCurrency(value: boolean) {
    this._isCurrency = value || false;
    this.updateDisplayValue();
  }

  @Input()
  set isPercentage(value: boolean) {
    this._isPercentage = value || false;
    this.updateDisplayValue();
  }

  @Input()
  set places(value: number) {
    this._places = value || 0;
    this.applyValidators();
    this.updateDisplayValue();
  }

  get places(): number {
    return this._places;
  }

  @Input()
  set displayPlaces(value: number) {
    this._displayPlaces = value || 0;
    this.applyValidators();
    this.updateDisplayValue();
  }

  get displayPlaces(): number {
    return this._displayPlaces;
  }

  @Input()
  set min(value: number) {
    this._min = value;
    this.applyValidators();
    this.updateDisplayValue();
  }

  get min(): number {
    return this._min;
  }

  @Input()
  set max(value: number) {
    this._max = value;
    this.applyValidators();
    this.updateDisplayValue();
  }

  get max(): number {
    return this._max;
  }

  @Input()
  set maxConstraint(value: string) {
    this._maxConstraint = value;
    this.applyValidators();
    this.updateDisplayValue();
  }

  @Input()
  set minConstraint(value: string) {
    this._minConstraint = value;
    this.applyValidators();
    this.updateDisplayValue();
  }

  @Input()
  set isNegative(value: boolean) {
    this._isNegative = value;
    this.updateDisplayValue();
  }

  get isNegative(): boolean {
    return this._isNegative;
  }

  @Input() 
  set isNotApplicable(value: boolean) {
    this._isNotApplicable = value;
    this.disabled = this._isNotApplicable;
    this.updateDisplayValue();
  }

  get isNotApplicable(): boolean {
    return this._isNotApplicable;
  }

  @Input() set disabled(v: boolean) {
    this._disabled = v;
    this.setDisabled();
  }

  get disabled(): boolean {
    return this._disabled;
  }

  constructor(
    private tts: TooltipService,
    private decimalPipe: DecimalPipe, 
    private negativeNumberPipe: NegativeNumberPipe,
    private cdr: ChangeDetectorRef) {}

  ngOnInit(): void {
    const validators = this.getValidators();
    this.inputControl = new FormControl(this._value, validators);

    this.inputControl.valueChanges.pipe(
      debounceTime(1000),
      distinctUntilChanged()
    ).subscribe(() => {
      this.onInputChange();
    });
  }
    
  ngAfterViewInit() {
    if(this.autofocus) {
      this.inputField.nativeElement.focus();
    }
    
    this.valueChange.emit(this._value);
    this.change.emit(this._value);

    setTimeout(() => {
      this.applyValidators();
      this.updateDisplayValue();
    });
  }

  applyValidators() {
    if (this.inputControl && this.inputControl.setValidators) {
      const v = this.getValidators();
      this.inputControl.setValidators(v);
      this.inputControl.updateValueAndValidity();
      this.onChange();
    }
  }

  getValidators() {
    const v: any = [];

    if(typeof this.min !== "undefined" && this.min !== null) {
      v.push(Validators.min(this.min));
    }

    if(typeof this.max !== "undefined" && this.max !== null) {
      v.push(Validators.max(this.max));
    }

    v.push(conditionalValidator(() => typeof this._maxConstraint !== "undefined" && this._maxConstraint !== null, (_c: FormControl) => {
      const hasValue = !(this._value === null || typeof this._value === "undefined" || this._value.toString() == "");
      const r = hasValue && (this._value > parseFloat(this._maxConstraint)) ? {'violatesMaxConstraint': {value: this._value}} : null;
      return r;
    }));

    v.push(conditionalValidator(() => typeof this._minConstraint !== "undefined" && this._minConstraint !== null, (_c: FormControl) => {
      const hasValue = !(this._value === null || typeof this._value === "undefined" || this._value.toString() == "");
      const r = hasValue && (this._value < parseFloat(this._minConstraint)) ? {'violatesMinConstraint': {value: this._value}} : null;
      return r;
    }));

    const p = (this.isEntryMode) ? this.places : this.displayPlaces || this.places || 0;
    v.push(Validators.pattern(new RegExp("^(-)?[0-9]+(\.[0-9]{0," + p + "})?$|^\.[0-9]{0," + p + "}$")));

    v.push(conditionalValidator(() => this._isRequired, (c: FormControl) => {
      if(c.touched) {
        const hasValue = !(this._value === null || typeof this._value === "undefined" || this._value.toString() == "");
        const r = !hasValue ? {'violatesRequired': {value: this._value}} : null;
        return r;
      }
      return null;
    }));

    v.push(conditionalValidator(() => p === 0, (_c: FormControl) => {
      const hasValue = !(this._value === null || typeof this._value === "undefined" || this._value.toString() == "");
      const r = hasValue && (this._value?.toString().indexOf('.') >= 0) ? {'violatesWholeNumbersOnly': {value: this._value}} : null;
      return r;
    }));

    return v;
  }

  updateDisplayValue() {  
    if(!this.inputControl) {
      return;
    }

    if(this.isNotApplicable) {
      this.displayValue = "n/a";
      this.cdr.detectChanges();
      return;
    }

    const p = this.displayPlaces || this.places || 0;
    
    if(!this.isEntryMode) {
      if (this._value === null) {
        this.displayValue = '';
        this.cdr.detectChanges();
        return;
      }

      // format of transform's second parameter is {minDigitsBeforeDecimal}.{minDigitsAfterDecimal}-{maxDigitsAfterDecimal}
      const formattedDecimalValue = this.decimalPipe.transform(this._value, '1.' + p + '-' + p);

      if (!(!this.inputControl.invalid || this.inputControl.disabled) || formattedDecimalValue === null) {
        return;
      }
      
      if(this.isNegative) {
        let prefix, suffix;

        if (this._isPercentage) {
          suffix = "%";
        } else if (this._isCurrency) {
          prefix = "$";
        }

        this.displayValue = this.negativeNumberPipe.transform(formattedDecimalValue, prefix, suffix, true);
      } else {
        if (this._isPercentage && formattedDecimalValue) {
          this.displayValue = formattedDecimalValue + '%';
        } else if (this._isCurrency && formattedDecimalValue) {
          this.displayValue = "$" + formattedDecimalValue;
        } else {
          this.displayValue = formattedDecimalValue || '';
        }
      }
    } else if (this._value !== null && typeof this._value !== "undefined") {
      const factor = Math.pow(10, p);
      const truncated = Math.floor(Number((this._value * factor).toFixed(8))) / factor;
      this.displayValue = truncated?.toLocaleString('en-US', { useGrouping: false, maximumFractionDigits: p});
    }
    
    this.cdr.detectChanges();
  }

  onBlur() {
    //this.onInputChange();
    this.isEntryMode = false;
    this.updateDisplayValue();

    this.applyChange.emit({
      value: this._value,
      isValid: !this.inputControl.invalid
    });
  }

  onFocus() {    
    this.isEntryMode = true;
    this.updateDisplayValue(); 
    this.applyValidators();   
    setTimeout(() => {
      if(this.inputField && this.inputField.nativeElement) {
        const p = this.displayPlaces || this.places || 0;
        this.inputField.nativeElement.setSelectionRange(0, this._value?.toLocaleString('en-US', { useGrouping: false, minimumFractionDigits: p}).length);
      }
    });
  }

  onKeyPress(event: KeyboardEvent) {
    // conditions that will prevent a character from being registered.
    if (this.isIllegalCharacter(event.key) || this.isIllegalNegative(event.key)) {
      event.preventDefault();
    }
  }

  onPaste(event: any) {
    let pasteVal: any = (event.clipboardData || (window as any).clipboardData).getData('text');
    pasteVal = pasteVal.trim();
    if (pasteVal.indexOf("\\r\\n") !== -1) {
        pasteVal = pasteVal.replace('\\r','').replace('\\n','');
        event.preventDefault();
        this.value = pasteVal;
        this.inputControl.setValue(pasteVal);
        this.blurInput();
        return;
    }

    let isIllegal = false;

    for (let i=0; i<pasteVal.length; i++) {
      if (this.isIllegalCharacter(pasteVal[i])) {
        isIllegal = true;
        break;
      }
    }
    if (isIllegal) {
      event.preventDefault();
    }
  }


  blurInput() {
    this.inputField.nativeElement.blur();
  }

  isIllegalCharacter(key: string) {
    return !['0', '1', '2', '3', '4', '5', '6', '7', '8', '9', '-', '.'].includes(key);
  }

  private isIllegalNegative(key: string): boolean {
    return key === '-' && ((this.min !== null && this.min >= 0) && (this.inputControl.value && this.inputControl.value?.toString().length > 0));
  }

  onChange() {
    if(this.inputControl) {
      if (this.inputControl.invalid && this.addZeroIfDotFirst && this.inputControl.value !== null) {
        this.addZeroInFrontOfFirstDot();
      }

      if(this.inputControl.invalid && (this._value !== null || this.isRequired)) {
        if(this.inputControl.hasError("min")) {
          this.tooltipMessage = this.min ? this.invalidRangeMessage ? this.invalidRangeMessage : 'Number must be ' + this.min + ' or greater' : 'Invalid range';
        } else if (this.inputControl.hasError("max")) {
          this.tooltipMessage = this.max ? this.invalidRangeMessage ? this.invalidRangeMessage : 'Number must be ' + this.max + ' or less' : 'Invalid range';
        } else if(this.inputControl.hasError("violatesMinConstraint")) {
          this.tooltipMessage = this.invalidMinConstraintMessage;        
        } else if(this.inputControl.hasError("violatesMaxConstraint")) {
          this.tooltipMessage = this.invalidMaxConstraintMessage;        
        } else if(this.inputControl.hasError("violatesRequired")) {
          this.tooltipMessage = this.invalidRequiredMessage;
        } else if(this.inputControl.hasError("violatesWholeNumbersOnly")) {
          this.tooltipMessage = 'Please enter whole numbers only';
        } else {
          this.tooltipMessage = this.invalidMessage;
        }
      } else {
        this.tooltipMessage = null;
        this.tts.remove();
      }
    }
  }

  onValueChange() {
      this.applyRounding();
      this.updateDisplayValue();
      this.applyValidators();
      this.change.emit(this._value);
      this.valueChange.emit(this._value);      
    }

  onInputChange() {
    this._value = this.inputControl.value;
    this.applyRounding();
    this.onChange();
    this.change.emit(this._value);
    this.valueChange.emit(this._value);
  }

  applyRounding() {
    if(this.rounding) {
      this._value = Math.round(this._value);
    }
  }

  private addZeroInFrontOfFirstDot() { // ex: .5
    if (!isNaN(this.inputControl.value) && this.inputControl.value !== '') {
      let val: string = this.inputControl.value.toString();
      const ifDotFirst = val.indexOf(".") === 0;

      if (ifDotFirst && val.length !== 1) {
        val = '0' + this.inputControl.value;
      }
      this._value = Number(val);
      this.inputControl.setValue(this._value);
    }
  }

  setDisabled() {
    if(this.inputControl) {
      this._disabled ? this.inputControl.disable() : this.inputControl.enable();
    }
  }
}
